import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "@/shared/i18n";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";
import type { Campaign, CreateCampaignPayload } from "../types";

const campaignFormSchema = z.object({
  title: z.string().min(1, "Título é obrigatório"),
  channel: z.enum(["email", "notification"]),
  recipientOption: z.enum(["all", "segment"]),
  subject: z.string().optional(),
  body: z.string().optional(),
  scheduledAt: z.string().nullable(),
});

export type CampaignFormValues = z.infer<typeof campaignFormSchema>;

function toFormValues(campaign: Campaign | null): CampaignFormValues {
  if (!campaign) {
    return {
      title: "",
      channel: "email",
      recipientOption: "all",
      subject: "",
      body: "",
      scheduledAt: null,
    };
  }
  const scheduledAt = campaign.scheduledAt
    ? campaign.scheduledAt.slice(0, 16)
    : null;
  return {
    title: campaign.title,
    channel: campaign.channel,
    recipientOption: campaign.recipientOption,
    subject: campaign.subject ?? "",
    body: campaign.body ?? "",
    scheduledAt,
  };
}

export interface CampaignFormProps {
  campaign: Campaign | null;
  isLoading?: boolean;
  onSubmit: (payload: CreateCampaignPayload) => Promise<void>;
  onCancel: () => void;
}

export function CampaignForm({
  campaign,
  isLoading = false,
  onSubmit,
  onCancel,
}: CampaignFormProps) {
  const { t } = useTranslation("bulk-notifications");

  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: toFormValues(campaign),
  });

  useEffect(() => {
    form.reset(toFormValues(campaign));
  }, [campaign, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    const payload: CreateCampaignPayload = {
      title: values.title,
      channel: values.channel,
      recipientOption: values.recipientOption,
      subject: values.subject || null,
      body: values.body || null,
      scheduledAt: values.scheduledAt
        ? new Date(values.scheduledAt).toISOString()
        : null,
    };
    await onSubmit(payload);
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("campaignTitle")}</FormLabel>
              <FormControl>
                <Input placeholder={t("campaignTitle")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="channel"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("channel")}</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={field.disabled}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t("channel")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="email">{t("channelEmail")}</SelectItem>
                  <SelectItem value="notification">
                    {t("channelNotification")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="recipientOption"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("recipients")}</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={field.disabled}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t("recipients")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="all">{t("recipientOptionAll")}</SelectItem>
                  <SelectItem value="segment">
                    {t("recipientOptionSegment")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("subject")}</FormLabel>
              <FormControl>
                <Input placeholder={t("subject")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="body"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("body")}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t("body")}
                  rows={5}
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="scheduledAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("scheduledAt")}</FormLabel>
              <FormControl>
                <Input
                  type="datetime-local"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(e.target.value ? e.target.value : null)
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            type="submit"
            disabled={isLoading}
            className="cf-btn-primary"
          >
            {isLoading ? t("loading") : t("save")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="cf-btn-outline text-[var(--cf-text)]"
            onClick={onCancel}
          >
            {t("cancel")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
